// src/components/TripHighlightsAndInfo.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCheck, FaTimes, FaChevronDown, FaChevronUp } from 'react-icons/fa';

// Styled Components
const Section = styled.section`
    padding: 2rem 0;
    background: #ffffff;
`;

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.75rem;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 1.5rem;
    position: relative;

    &:after {
        content: '';
        position: absolute;
        bottom: -0.5rem;
        left: 0;
        width: 50px;
        height: 3px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        border-radius: 2px;
    }
`;

// Full Description Section
const DescriptionSection = styled.div`
    margin-bottom: 3rem;
`;

const DescriptionContent = styled.div`
    background: #f8fafc;
    border-radius: 12px;
    padding: 2rem;
    border: 1px solid #e5e7eb;
`;

const DescriptionText = styled.div`
    color: #666;
    line-height: 1.6;
    font-size: 1rem;

    p {
        margin-bottom: 1rem;

        &:last-child {
            margin-bottom: 0;
        }
    }
`;

// Highlights Section
const HighlightsSection = styled.div`
    margin-bottom: 3rem;
`;

const HighlightsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
`;

const HighlightCard = styled(motion.div)`
    background: white;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    }
`;

const HighlightImage = styled.div<{ bgImage: string }>`
    height: 160px;
    background:
            linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1)),
            url(${props => props.bgImage}) center/cover;
`;

const HighlightContent = styled.div`
    padding: 1.25rem;
`;

const HighlightTitle = styled.h3`
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #1a1a1a;
    line-height: 1.4;
`;

// Info Grid Section
const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
`;

const InfoCard = styled(motion.div)`
    background: #f8fafc;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #e5e7eb;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
`;

const InfoCardTitle = styled.h3`
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #1a1a1a;
    position: relative;

    &:after {
        content: '';
        position: absolute;
        bottom: -0.25rem;
        left: 0;
        width: 30px;
        height: 2px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        border-radius: 1px;
    }
`;

const InfoList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;

    li {
        padding: 0.5rem 0;
        border-bottom: 1px solid #e5e7eb;
        color: #666;
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        font-size: 0.9rem;
        line-height: 1.4;

        &:last-child {
            border-bottom: none;
        }
    }
`;

const IncludedList = styled(InfoList)``;

const NotIncludedList = styled(InfoList)``;

const NotSuitableList = styled(InfoList)`
    li:before {
        content: '•';
        color: #667eea;
        font-weight: bold;
        font-size: 1rem;
        line-height: 1;
        flex-shrink: 0;
    }
`;

const WhatToBringList = styled(InfoList)`
    li:before {
        content: '•';
        color: #667eea;
        font-weight: bold;
        font-size: 1rem;
        line-height: 1;
        flex-shrink: 0;
    }
`;

const NotAllowedList = styled(InfoList)`
    li:before {
        content: '✕';
        color: #ef4444;
        font-weight: bold;
        font-size: 0.8rem;
        flex-shrink: 0;
        margin-top: 0.1rem;
    }
`;

const KnowBeforeYouGoList = styled(InfoList)`
    li:before {
        content: '💡';
        color: #f59e0b;
        flex-shrink: 0;
        margin-top: 0.1rem;
        font-size: 0.8rem;
    }
`;

const ListItemContent = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
`;

// @ts-ignore
const CheckIcon = styled(FaCheck)`
    color: #10b981;
    font-size: 0.9rem;
    flex-shrink: 0;
    margin-top: 0.1rem;
`;

// @ts-ignore
const CrossIcon = styled(FaTimes)`
    color: #ef4444;
    font-size: 0.9rem;
    flex-shrink: 0;
    margin-top: 0.1rem;
`;

const ExpandableSection = styled.div`
    margin-top: 1rem;
`;

const ExpandButton = styled.button`
    background: none;
    border: none;
    color: #667eea;
    font-weight: 600;
    cursor: pointer;
    padding: 0.5rem 0;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: color 0.2s ease;

    &:hover {
        color: #764ba2;
    }
`;

// @ts-ignore
const ChevronIcon = styled(FaChevronDown)`
    font-size: 0.8rem;
`;

// Component
const TripHighlightsAndInfo: React.FC = () => {
    const [showAllNotAllowed, setShowAllNotAllowed] = useState(false);

    const highlights = [
        {
            title: "Explore Červený Kameň: Slovakia's Best-Preserved Renaissance Castle",
            image: "/img_19.png"
        },
        {
            title: "Step into the fairy-tale Smolenice Castle, a neo-Gothic château in the Little Carpathians",
            image: "/smolenice-castle.png"
        },
        {
            title: "Discover the Living Masterpiece: The Slovak Bethlehem in Rajecká Lesná",
            image: "/img_24.png"
        }
    ];

    const included = [
        "Transportation in a comfortable air-conditioned vehicle",
        "Transport from the hotel and back",
        "Live guide",
        "English-speaking guide",
        "Wine tasting (optional)",
        "Bottled water available",
        "Visit to the Červený Kameň Castle",
        "Visit to the Smolenice Castle",
        "Visit to the village of Čičmany",
        "Entrance fee to the Bratislava Castle Observation (12€ per person)",
        "Free time in Červený Kameň, Trenčín, Čičmany, Bratislava Castle"
    ];

    const notIncluded = [
        "Meals"
    ];

    const notSuitableFor = [
        "Children under 4 years",
        "People with mobility impairments",
        "Wheelchair users",
        "People over 309 lbs (140 kg)"
    ];

    const whatToBring = [
        "Comfortable shoes",
        "Camera",
        "Weather-appropriate clothing"
    ];

    const notAllowed = [
        "Pets",
        "Weapons or sharp objects",
        "Mobility scooters",
        "Smoking in the vehicle",
        "Swimwear",
        "Non-folding strollers",
        "Smoking indoors",
        "Electric wheelchairs",
        "Firework",
        "Explosive substances",
        "Making fire"
    ];

    const knowBeforeYouGo = [
        "The tour involves walking and hiking, so wear comfortable shoes.",
        "Weather can vary, so dress appropriately.",
        "Bring a camera for the scenic views and historical sites."
    ];

    const displayedNotAllowed = showAllNotAllowed ? notAllowed : notAllowed.slice(0, 5);

    return (
        <Section>
            <Container>
                {/* Full Description Section - First */}
                <DescriptionSection>
                    <SectionTitle>Full Description</SectionTitle>
                    <DescriptionContent>
                        <DescriptionText>
                            <p>
                                Begin your journey in the Small Carpathians at Červený Kameň Castle, one of the best-preserved Renaissance castles in Slovakia. Nestled in the forests near the town of Častá, the castle is renowned for its vast underground cellars, richly furnished interiors and noble heritage.
                            </p>
                            <p>
                                Once the seat of the Pálffy family, it offers visitors an authentic glimpse into the life of aristocrats, with original furniture, hunting trophies and historical armor on display. Stroll through the peaceful courtyards and the surrounding castle park to begin the day immersed in history and elegance.
                            </p>
                            <p>
                                From there, head to Smolenice Castle, a romantic neo-Gothic château crowning a wooded hill at the foot of the Little Carpathians. Built by the aristocratic Pálffy family on the foundations of a medieval guard castle that once watched over a mountain pass, Smolenice looks like something out of a fairy tale, with its slender tower, knights' hall and sweeping English-style park. Today the castle belongs to the Slovak Academy of Sciences, and a stroll through its grounds offers postcard views over the surrounding hills and forests.
                            </p>
                            <p>
                                Next, visit Trenčín Castle, the crown jewel of the region and one of Slovakia's largest castles. Towering above the town of Trenčín, the castle complex includes courtyards, a museum, a gothic chapel and the famous Matúš Tower. It served as the seat of Matúš Čák, a powerful medieval nobleman. Inside, visitors can explore displays of medieval life, armor and local legends. The view from the top stretches across the Váh River and the town below.
                            </p>
                            <p>
                                Leaving the stone castles behind, journey into the hills to the village of Čičmany, known for its wooden houses painted with traditional white motifs. These geometric patterns once served a protective role but now stand as symbols of Slovak folk identity. Čičmany feels like a living museum where time has slowed. Walking through its quiet streets and visiting local museums provides a deep connection to Slovak traditions, costumes and craftsmanship.
                            </p>
                            <p>
                                <strong>Slovak Bethlehem – A Living Wooden Masterpiece</strong><br />
                                Step into the magical world of the Slovak Bethlehem in Rajecká Lesná – the largest movable wooden Bethlehem in Europe. Spanning over eight meters, it features around 300 hand-carved figures, more than half in motion, bringing the Nativity story and centuries of Slovak life to life before your eyes. It's not just a scene – it's a journey through the heart and soul of Slovakia, captured forever in wood.
                            </p>
                            <p>
                                End your day in Bratislava, the capital of Slovakia, at Bratislava Castle, the city's iconic four-towered landmark rising on a hill above the Danube. From its ramparts and terraces you'll take in a full sweep of the Old Town, the river and the rooftops below, with views reaching across to Austria on clear days. Once a royal residence and now home to the Slovak National Museum, the castle is the perfect place to watch the sun set over the Danube — a serene and unforgettable finale to a day rich in adventure and discovery.
                            </p>
                        </DescriptionText>
                    </DescriptionContent>
                </DescriptionSection>

                {/* Highlights Section - Second */}
                <HighlightsSection>
                    <SectionTitle>Highlights</SectionTitle>
                    <HighlightsGrid>
                        {highlights.map((highlight, index) => (
                            <HighlightCard
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -3 }}
                            >
                                <HighlightImage bgImage={highlight.image} />
                                <HighlightContent>
                                    <HighlightTitle>{highlight.title}</HighlightTitle>
                                </HighlightContent>
                            </HighlightCard>
                        ))}
                    </HighlightsGrid>
                </HighlightsSection>

                {/* Information Grid - Third */}
                <div>
                    <SectionTitle>Additional Information</SectionTitle>
                    <InfoGrid>
                        {/* Column 1: Included & Not Included */}
                        <InfoCard
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.1 }}
                            viewport={{ once: true }}
                        >
                            <InfoCardTitle>Included</InfoCardTitle>
                            <IncludedList>
                                {included.map((item, index) => (
                                    <li key={index}>
                                        <ListItemContent>
                                            <CheckIcon />
                                            <span>{item}</span>
                                        </ListItemContent>
                                    </li>
                                ))}
                            </IncludedList>

                            <InfoCardTitle style={{ marginTop: '2rem' }}>Not Included</InfoCardTitle>
                            <NotIncludedList>
                                {notIncluded.map((item, index) => (
                                    <li key={index}>
                                        <ListItemContent>
                                            <CrossIcon />
                                            <span>{item}</span>
                                        </ListItemContent>
                                    </li>
                                ))}
                            </NotIncludedList>

                            <InfoCardTitle style={{ marginTop: '2rem' }}>Not Suitable For</InfoCardTitle>
                            <NotSuitableList>
                                {notSuitableFor.map((item, index) => (
                                    <li key={index}>{item}</li>
                                ))}
                            </NotSuitableList>
                        </InfoCard>

                        {/* Column 2: Important Information */}
                        <InfoCard
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.2 }}
                            viewport={{ once: true }}
                        >

                            <InfoCardTitle style={{ fontSize: '1.1rem', margin: '1.5rem 0 1rem 0' }}>What to Bring</InfoCardTitle>
                            <WhatToBringList>
                                {whatToBring.map((item, index) => (
                                    <li key={index}>{item}</li>
                                ))}
                            </WhatToBringList>

                            <InfoCardTitle style={{ fontSize: '1.1rem', margin: '1.5rem 0 1rem 0' }}>Not Allowed</InfoCardTitle>
                            <NotAllowedList>
                                {displayedNotAllowed.map((item, index) => (
                                    <li key={index}>{item}</li>
                                ))}
                            </NotAllowedList>

                            {notAllowed.length > 5 && (
                                <ExpandableSection>
                                    <ExpandButton
                                        onClick={() => setShowAllNotAllowed(!showAllNotAllowed)}
                                    >
                                        {showAllNotAllowed ? 'See less' : 'See more'}
                                        <ChevronIcon style={{ transform: showAllNotAllowed ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                                    </ExpandButton>
                                </ExpandableSection>
                            )}
                        </InfoCard>

                        {/* Column 3: Know Before You Go */}
                        <InfoCard
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.3 }}
                            viewport={{ once: true }}
                        >
                            <InfoCardTitle>Know Before You Go</InfoCardTitle>
                            <KnowBeforeYouGoList>
                                {knowBeforeYouGo.map((item, index) => (
                                    <li key={index}>{item}</li>
                                ))}
                            </KnowBeforeYouGoList>
                        </InfoCard>
                    </InfoGrid>
                </div>
            </Container>
        </Section>
    );
};

export default TripHighlightsAndInfo;